import type { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import { CHART_COLORS, chartColor, type ChartDatum } from "@/shared/components/ds";
import type { ChartData, ChartSeries, PieSeries, PieSlice } from "../models/dashboard";
import type { TileSpec } from "./tiles";
import styles from "./dashboard.module.css";
import pageStyles from "../opLaunchPages.module.css";

/*
    The OP Launch Dashboard's chart shapes (MPN-2230), each taking a tile's `chart_data` as the
    server sends it. Distributions are the design system's pie and bars; the time series stay
    on react-apexcharts, restyled to the tokens. Emptiness is decided here too, so the widget
    shell can collapse to "No data for this launch".
*/

export const money = (v: number | null | undefined, cents = false) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 }).format(v ?? 0);
export const thousands = (v: number | null | undefined) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(v ?? 0);

function colorsOf(items: { color?: string | null }[] | null | undefined): string[] {
    const colors = (items ?? []).map((i) => i.color).filter((c): c is string => !!c);
    return colors.length === (items ?? []).length && colors.length ? colors : CHART_COLORS;
}

// --- time series -------------------------------------------------------------------------

function timeSeries(data: ChartData): ChartSeries[] {
    return ((data.series_data ?? []) as ChartSeries[]).filter((s) => s.data?.length);
}

export function hasTimeSeries(data: ChartData): boolean {
    return timeSeries(data).length > 0;
}

/** Stacked area (spend, impressions) or the plain line of impact-trendline, on the tokens. */
export function TimeSeriesChart({ data, tile }: { data: ChartData; tile: TileSpec }) {
    const series = timeSeries(data);
    const format = tile.money ? (v: number) => money(v, true) : thousands;
    const area = tile.kind === "area";
    const options: ApexOptions = {
        chart: {
            type: area ? "area" : "line", stacked: area, zoom: { enabled: false }, toolbar: { show: false },
            animations: { enabled: false }, fontFamily: "Inter, -apple-system, 'Segoe UI', sans-serif", foreColor: "#64748b",
        },
        colors: colorsOf(series),
        dataLabels: { enabled: false },
        stroke: { curve: "straight", width: area ? 1 : 2 },
        // Only set for areas: a `fill: undefined` key replaces ApexCharts' default fill object and the line renderer throws on fill.colors (MPN-2339).
        ...(area ? { fill: { type: "solid", opacity: 0.75 } } : {}),
        grid: { borderColor: "#f1f5f9", strokeDashArray: 0 },
        xaxis: { type: "datetime", axisBorder: { color: "#e8edf3" }, axisTicks: { color: "#e8edf3" }, labels: { style: { fontSize: "11px" } } },
        yaxis: { title: { text: tile.yTitle, style: { fontSize: "10px", fontWeight: 700, color: "#94a3b8" } }, labels: { formatter: (v) => format(v), style: { fontSize: "11px" } } },
        tooltip: {
            shared: true,
            x: { format: "yyyy-MM-dd" },
            y: {
                formatter: (value, { seriesIndex, dataPointIndex }) => {
                    // impressions-per-constituent-by-vector carries the text to show per point.
                    const point = series[seriesIndex]?.data?.[dataPointIndex];
                    if (point?.label) return point.label;
                    return value ? format(value) : "NA";
                },
            },
        },
        legend: { position: "bottom", fontSize: "11px", markers: { size: 4 } },
    };
    const apexSeries = series.map((s) => ({
        name: s.name ?? "",
        data: (s.data ?? []).map((p) => ({ x: p.x, y: p.y })),
    }));
    return (
        <div className={styles.chart}>
            <ReactApexChart options={options} series={apexSeries} type={area ? "area" : "line"} height={220} />
        </div>
    );
}

// --- distributions -----------------------------------------------------------------------

function slices(data: ChartData): PieSlice[] {
    const first = (data.series_data ?? [])[0] as PieSeries | undefined;
    return (first?.data ?? []).filter((s) => (s.y ?? 0) > 0);
}

export function hasSlices(data: ChartData): boolean {
    return slices(data).length > 0;
}

/** The slices as the design system's chart data, on its colour ladder. */
export function distribution(data: ChartData, tile: TileSpec): ChartDatum[] {
    const format = tile.money ? (v: number) => money(v) : thousands;
    return slices(data).map((s, i) => ({ label: s.name ?? "", value: s.y ?? 0, display: format(s.y ?? 0), color: (tile.ownColors && s.color) || chartColor(i) }));
}

export function distributionTotal(data: ChartData, tile: TileSpec): string {
    const total = slices(data).reduce((sum, s) => sum + (s.y ?? 0), 0);
    return `${tile.money ? money(total) : thousands(total)} total`;
}

// --- events-dates: the launch's days -----------------------------------------------------

export function hasDataPoints(data: ChartData): boolean {
    return (data.data_points ?? []).some((p) => (p.y ?? 0) > 0);
}

/** A ring of days live over the launch's days, the legend beside it, the server's captions beneath. */
export function LaunchDatesRing({ data }: { data: ChartData }) {
    const points = (data.data_points ?? []).filter((p) => (p.y ?? 0) > 0);
    const total = points.reduce((sum, p) => sum + (p.y ?? 0), 0) || 1;
    const liveIndex = Math.max(0, points.findIndex((p) => /live|serv/i.test(p.name ?? "")));
    const live = points[liveIndex]?.y ?? 0;
    const C = 2 * Math.PI * 48;
    return (
        <>
            <div className={styles.ringRow}>
                <div className={styles.ring}>
                    <svg width="116" height="116" viewBox="0 0 116 116" role="img" aria-label={`${live} days live of ${total}`}>
                        <circle cx="58" cy="58" r="48" fill="none" style={{ stroke: "var(--slate-100)" }} strokeWidth="12" />
                        <circle cx="58" cy="58" r="48" fill="none" style={{ stroke: "var(--moti-primary)" }} strokeWidth="12" strokeLinecap="round"
                            strokeDasharray={`${(C * Math.min(1, live / total)).toFixed(1)} ${C.toFixed(1)}`} transform="rotate(-90 58 58)" />
                    </svg>
                    <div className={styles.ringCenter}>
                        <span className={styles.ringBig}>{thousands(live)}</span>
                        <span className={styles.ringCaption}>days live</span>
                    </div>
                </div>
                <div className={styles.legend}>
                    {points.map((p, i) => (
                        <div key={i} className={styles.legendRow}>
                            <span className={styles.dot} style={{ background: i === liveIndex ? "var(--moti-primary)" : chartColor(i + 1) }} />
                            <span className={styles.legendLabel}>{p.name}</span>
                            <span className={styles.legendValue}>{thousands(p.y)}</span>
                        </div>
                    ))}
                </div>
            </div>
            {(data.chart_inner_label || data.subtitle) && (
                <div className={`${styles.note} ${pageStyles.html}`}>
                    {/* Server HTML from the frozen OP warehouse, as the table cells are. */}
                    {data.chart_inner_label && <div dangerouslySetInnerHTML={{ __html: data.chart_inner_label }} />}
                    {data.subtitle && <div dangerouslySetInnerHTML={{ __html: data.subtitle }} />}
                </div>
            )}
        </>
    );
}

/** The server's captions under a tile (constituents-served, budget-summary): its HTML, as the table cells are. */
export function Captions({ data }: { data: ChartData }) {
    if (!data.inner_label && !data.subtitle) return null;
    return (
        <div className={`${styles.note} ${pageStyles.html}`}>
            {data.inner_label && <div dangerouslySetInnerHTML={{ __html: data.inner_label }} />}
            {data.subtitle && <div dangerouslySetInnerHTML={{ __html: data.subtitle }} />}
        </div>
    );
}

// --- constituents-served -----------------------------------------------------------------

function rings(data: ChartData): PieSlice[] {
    return (data.series ?? []).map((s) => s.data?.[0]).filter((p): p is PieSlice => !!p);
}

export function hasRings(data: ChartData): boolean {
    return rings(data).some((r) => (r.y ?? 0) > 0);
}

/** Max / Current / Min served, by name: the current count large, the other two as a pair. */
export function ServedSummary({ data }: { data: ChartData }) {
    const points = rings(data);
    const byName = (re: RegExp) => points.find((p) => re.test(p.name ?? ""));
    const max = byName(/max/i);
    const min = byName(/min/i);
    const current = byName(/current|now|today/i) ?? points.find((p) => p !== max && p !== min) ?? points[0];
    return (
        <>
            <div className={styles.served}>
                <div>
                    <div className={styles.servedBig}>{thousands(current?.y)}</div>
                    <div className={styles.servedSub}>{current?.name || "Served"} since launch</div>
                </div>
                {(max || min) && (
                    <div className={styles.servedPair}>
                        <div className={styles.servedCell}><div className={styles.micro}>{max?.name ?? "Max Served"}</div><div className={styles.servedValue}>{max ? thousands(max.y) : "—"}</div></div>
                        <div className={styles.servedCell}><div className={styles.micro}>{min?.name ?? "Min Served"}</div><div className={styles.servedValue}>{min ? thousands(min.y) : "—"}</div></div>
                    </div>
                )}
            </div>
            <Captions data={data} />
        </>
    );
}
